import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, IconButton, Typography, Button } from "@mui/material";
import { CalendarDays, Plus, CalendarCheck } from "lucide-react";
import { ReactComponent as SparklesIcon } from "../../../icons/Advisor/Sparkles.svg";
import { Family } from "protogen/advisors_service_pb";
import { RunWorkflowRequest } from "protogen/workflow_service_pb";
import { useRunWorkflow } from "services/workflow";
import { useWebSocketContext } from "components/context/SocketConsumerProvider";
import AILoading from "components/common/AILoading";
import FlexBox from "components/common/FlexBox";
import LinkRouter from "components/navigation/LinkRouter";
import useIsMobile from "components/hooks/useIsMobile";
import { CurrentUserContext } from "components/context/RequireAuth";
import { planFlagsFromStatus } from "utils/memberFamilyPlan";
import { calendarTasksConnectCalendarSecondLine } from "common/memberModuleResolver";

type CalendarEvent = {
  title: string;
  date: string;
  tasks: string[];
  eventRef: string;
  eventSourceType: string;
};

const MAX_EVENTS = 5;

const ConnectCalendarLink = () => {
  const navigate = useNavigate();
  const currentUser = useContext(CurrentUserContext);
  const planFlags = planFlagsFromStatus(currentUser.familyStatus);
  return (
    <Box
      sx={{
        padding: "22px 24px",
        display: "flex",
        flexDirection: "column",
        gap: "18px",
      }}
    >
      <CalendarCheck size={40} style={{ color: "#198282" }} />
      <Typography variant="h4">Unlock calendar suggestions</Typography>
      <Typography variant="body">
        With a connected calendar, you'll get access to personalized task
        suggestions based on upcoming events.
      </Typography>
      <Typography variant="body">
        {calendarTasksConnectCalendarSecondLine(planFlags)}
      </Typography>
      <Box>
        <Button
          variant="outlined"
          onClick={() => {
            navigate("/settings/calendars?import=true");
          }}
        >
          Connect your calendar
        </Button>
      </Box>
    </Box>
  );
};

export default ({
  family,
  integrationCount,
  forceLoading = false,
}: {
  family: Family;
  integrationCount: number;
  forceLoading: boolean;
}) => {
  const navigate = useNavigate();
  const { registerHandler, unregisterHandler } = useWebSocketContext();
  const [events, _setEvents] = useState<CalendarEvent[]>([]);

  const setEvents = (newEvents: CalendarEvent[]) => {
    _setEvents(newEvents.slice(0, MAX_EVENTS));
  };
  const [loading, setLoading] = useState(false);
  const { request: workflowRequest } = useRunWorkflow();
  const isLoading = forceLoading || loading;

  const isMobile = useIsMobile();

  useEffect(() => {
    const handler = (channel: string, eventData: any) => {
      setLoading(false);
      if (eventData && Array.isArray(eventData)) {
        setEvents(eventData);
      } else {
        setEvents([]);
      }
    };

    registerHandler("calendar-task-suggestions/", handler);
    return () => {
      unregisterHandler("calendar-task-suggestions/", handler);
    };
  }, []);

  useEffect(() => {
    setEvents([]);
    generateCalendarTasks();
  }, [family, integrationCount]);

  const generateCalendarTasks = async () => {
    if (!family?.ref || isLoading || !integrationCount) return;
    setLoading(true);
    setEvents([]);

    const request = new RunWorkflowRequest({
      workflowName: "family-calendar-task-suggestions",
      arguments: [
        {
          name: "integration_count",
          value: { case: "intValue", value: integrationCount + 1 },
        },
      ],
      entityType: 0,
      entityRef: family.ref,
      runAsync: false,
    });

    const result = await workflowRequest(request);
    if (result) {
      const data = JSON.parse(result.result);
      if (data.events && Array.isArray(data.events) && data.events.length > 0) {
        setEvents(data.events);
      } else {
        setEvents([]);
      }
    } else {
      setEvents([]);
    }
    setLoading(false);
  };

  const onTaskClick = (title: string, event: CalendarEvent) => {
    navigate("/tasks/add", {
      state: {
        taskTitle: title,
        family: family,
        calendarEventRef: event.eventRef,
        calendarEventSourceType: event.eventSourceType,
      },
    });
  };

  if (!isLoading && !integrationCount) {
    return <ConnectCalendarLink />;
  }

  return (
    <FlexBox direction="column" gap="18px">
      {/* Header */}
      <FlexBox alignItems="center" sx={{ width: "100%", flex: 0 }}>
        <FlexBox gap="8px" alignItems="center">
          <SparklesIcon height="20px" width="20px" />
          <Typography
            variant="h4"
            color="#3D3D3D"
            sx={{
              display: "flex",
            }}
          >
            Tasks for your upcoming events
          </Typography>
        </FlexBox>
      </FlexBox>

      {/* Content - Loading, Events, or Empty State */}
      <FlexBox
        variant={isMobile ? "contained" : "default"}
        direction="column"
        sx={{
          ...(isMobile ? { paddingLeft: "12px", paddingTop: "12px" } : {}),
        }}
      >
        {isLoading ? (
          <FlexBox justifyContent="center" sx={{ marginBottom: "15%" }}>
            <AILoading messages="calendar" />
          </FlexBox>
        ) : events.length > 0 ? (
          <FlexBox direction="column" gap="4px">
            {events.map((event, index) => (
              <Box key={index}>
                {/* Event Header */}
                <FlexBox alignItems="start" gap="6px">
                  <CalendarDays
                    size={20}
                    style={{
                      color: "#8E9598",
                      flexShrink: 0,
                      minWidth: "20px",
                      minHeight: "20px",
                      marginTop: "2px",
                    }}
                  />
                  <FlexBox
                    sx={{
                      flexWrap: "wrap",
                      gap: "4px",
                      alignItems: "center",
                      rowGap: "0",
                    }}
                  >
                    <LinkRouter
                      to={`/calendar/${event.eventRef}${
                        event.eventSourceType === "external"
                          ? "?external=true"
                          : ""
                      }`}
                    >
                      <Typography variant="bodyHeavy" color="#3D3D3D">
                        {event.title}
                      </Typography>
                    </LinkRouter>
                    <Typography
                      variant="bodySmall"
                      sx={{
                        color: "#198282",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {event.date}
                    </Typography>
                  </FlexBox>
                </FlexBox>

                {/* Tasks List */}
                <Box
                  sx={{
                    marginLeft: "9px",
                    borderLeft: "2px solid #D4D4D4",
                    paddingLeft: "12px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    marginTop: "4px",
                    paddingBottom: index === events.length - 1 ? "0" : "8px",
                  }}
                >
                  {event.tasks.map((task, taskIndex) => (
                    <FlexBox key={taskIndex} alignItems="flex-start" gap="8px">
                      <IconButton onClick={() => onTaskClick(task, event)}>
                        <Plus
                          size={20}
                          strokeWidth={2}
                          style={{ color: "#198282" }}
                        />
                      </IconButton>
                      <Typography variant="body" color="#3D3D3D">
                        {task}
                      </Typography>
                    </FlexBox>
                  ))}
                </Box>
              </Box>
            ))}
          </FlexBox>
        ) : (
          <Box
            sx={{
              padding: "24px",
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
              backgroundColor: "#FAFAFA",
              textAlign: "center",
            }}
          >
            <Typography variant="body" color="#666666">
              No upcoming events found. Check back later for task suggestions.
            </Typography>
          </Box>
        )}
      </FlexBox>

      {/* Footer Link */}
      {!isLoading && (
        <Box>
          <LinkRouter sx={{ color: "#198282" }} to={"/calendar"}>
            See full calendar
          </LinkRouter>
        </Box>
      )}
    </FlexBox>
  );
};
